<template>
  <Component :is="viewLayout">
    <template #page-heading>
      <h1 class="heading heading--large">
        Leads
      </h1>
    </template>

    <template #page-filter>
      <CommonPageFilter
        :filters="[
          'name',
          'email',
          'cpf',
          'phone',
          'dateRange',
          'action',
          'plugin',
          'product',
          'status',
          'csat'
        ]"
      />
    </template>

    <template #main>
      <div class="leads">
        <template v-if="pageItems.total">
          <div class="leads__table-wrapper">
            <table class="leads__table">
              <thead>
                <tr>
                  <th
                    v-for="column in columnsActive"
                    :key="column.id"
                    v-show="column.isVisible"
                  >
                    {{ column.label }}
                  </th>

                  <th
                    class="leads__table__column-customize"
                    :class="{ 'leads__table__column-customize--open': isOpenColumnCustomize}"
                  >
                    <button
                      class="leads__table__column-customize__trigger"
                      @click.prevent="toggleCustomizeColumn"
                      v-click-outside="closeCustomizeColumn"
                    >
                      <span>
                        +
                      </span>
                    </button>

                    <div
                      class="leads__table__column-customize__menu"
                      @click.stop
                    >
                      <h4 class="leads__table__column-customize__heading">
                        Exibir colunas
                      </h4>

                      <ul class="leads__table__column-customize__list">
                        <li
                          v-for="column in columnsActive"
                          :key="column.id"
                          class="leads__table__column-customize__list__item"
                          :class="{
                            'leads__table__column-customize__list__item--disabled': column.id === 1,
                            'leads__table__column-customize__list__item--selected': column.isVisible
                          }"
                          @click.stop="chooseColumn(column.id)"
                        >
                          {{ column.label }}
                        </li>
                      </ul>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in table.rows"
                  :key="row.lead"
                >
                  <td
                    v-for="column in columnsActive"
                    :key="column.id"
                    v-show="column.isVisible"
                    :class="`leads__table__column-${column.id}`"
                  >
                    <!-- Lead -->
                    <template v-if="column.id === 1">
                      <div class="leads__table__column-1__actions">
                        <button
                          class="leads__table__column-1__button leads__table__column-1__button--details"
                          @click.prevent="openLeadDetails(row)"
                          v-tooltip="{
                            theme: 'tooltip-top-right',
                            content: 'Ver detalhes<br>do lead.'
                          }"
                        >
                          <SvgIconInfo/>
                        </button>

                        <button
                          class="leads__table__column-1__button leads__table__column-1__button--conversation"
                          @click.prevent="openLeadHistory(row)"
                          v-tooltip="{
                            theme: 'tooltip-top-right',
                            content: 'Ver histórico<br>de conversação.'
                          }"
                        >
                          <SvgIconChat/>

                          <span>
                            Ver conversas
                          </span>
                        </button>
                      </div>

                      <div class="leads__table__column-1__name">
                        {{ row['name'] }}
                      </div>
                    </template>

                    <!-- Produto -->
                    <template v-if="column.id === 2  ">
                      <div class="leads__table__column-2__description">
                        {{ getProduct(row) || '-' }}
                      </div>
                    </template>

                    <!-- Status -->
                    <template v-if="column.id === 3">
                      <button
                        class="leads__table__column-3__tag tag-status tag-status--link"
                        :class="`tag-status--${row['clh_last-wc-fk_id_status']}`"
                        @click="openLeadJourney(row)"
                        v-tooltip="{
                          theme: 'tooltip-left',
                          content: getStatusTooltip(row['clh_last-wc-fk_id_status'])
                        }"
                      >
                        {{ row['cs_status-wc-label'] }}
                      </button>

                      <div
                        v-if="row['clh_last-wc-fk_id_status'] === 3 && row['value']"
                        class="leads__table__column-3__amount"
                      >
                        {{ row['value'] | currency }}
                      </div>

                      <div class="leads__table__column-3__when">
                        {{ row['clh_last-wc-created_at'] | date }}
                      </div>
                    </template>

                    <!-- Sentimento -->
                    <template v-if="column.id === 4">
                      <div class="leads__table__column-4__label">
                        {{ getFeeling(row['ams-general_sentiment']) }}
                      </div>
                    </template>

                    <!-- Csat -->
                    <template v-if="column.id === 5">
                      <div
                        v-if="row['cae-wc-evaluation']"
                        class="leads__table__column-5__rating"
                        :class="`leads__table__column-5__rating--${row['cae-wc-evaluation']}`"
                        :title="`Nota ${row['cae-wc-evaluation']}`"
                      >
                        <div
                          v-for="i in 5"
                          :key="i"
                          class="leads__table__column-5__rating__star"
                        />
                      </div>

                      <template v-else>
                        -
                      </template>
                    </template>

                    <!-- Origem -->
                    <template v-if="column.id === 6">
                      <div
                        v-if="row['clh_lead-wc-origin']"
                        class="leads__table__column-6__origin"
                      >
                        {{ row['clh_lead-wc-origin'] }}
                      </div>

                      <a
                        v-if="row['clh_lead-wc-referer']"
                        :href="row['clh_lead-wc-referer']"
                        target="_blank"
                        class="leads__table__column-6__referer"
                      >
                        {{ row['clh_lead-wc-referer'] }}
                      </a>
                    </template>

                    <!-- Plugin -->
                    <template v-if="column.id === 7">
                      <div class="leads__table__column-7__label">
                        {{ row['clh_lead-wc-plugin'] }}
                      </div>
                    </template>

                    <!-- Dados pessoais -->
                    <template v-if="column.id === 8">
                      <div class="leads__table__column-8__id">
                        <strong>ID:</strong> {{ row['cl-wc-id_lead'] }}
                      </div>

                      <div
                        v-if="row['phone']"
                        class="leads__table__column-8__phone">
                        <strong>Fone:</strong> {{ row['phone'] | phone }}
                      </div>

                      <div
                        v-if="row['email']"
                        class="leads__table__column-8__email">
                        <strong>E-mail:</strong> {{ row['email'] }}
                      </div>
                    </template>
                  </td>

                  <td/>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="leads__page-items">
            <div class="leads__page-items__info">
              {{ pageItemsMessage }}
            </div>

            <button
              class="leads__page-items__more"
              :class="{ 'leads__page-items__more--off': isLocalLoading }"
              @click.prevent="fetchMorePageItems"
              :disabled="!hasMorePageItems"
            >
              <BaseLoaderDefault v-if="isLocalLoading"/>

              <span v-else>
                Mostrar mais
              </span>
            </button>
          </div>
        </template>

        <BasePaneCard
          v-else
          class="height-full"
        >
          <BaseEmptyState>
            <SvgIconGroups/>

            <h3>Nenhum lead para mostrar.</h3>
            <p>Tente novamente alterando os critérios do filtro.</p>
          </BaseEmptyState>
        </BasePaneCard>

        <CommonLeadDetails
          :isOpen="isOpenLeadDetails"
          :heading="leadDetailsHeading"
          :subheading="leadDetailsSubheading"
          :leadId="leadSelected.id"
        />

        <CommonLeadHistory
          :isOpen="isOpenLeadHistory"
          :heading="leadHistoryHeading"
          :subheading="leadHistorySubheading"
          :leadId="leadSelected.id"
          :historyId="leadSelected.historyId"
        />

        <CommonModalLeadJourney
          :leadId="leadSelected.id"
          :statusId="leadSelected.statusId"
          :statusLabel="leadSelected.statusLabel"
          :fullName="leadSelected.fullName"
          :product="leadSelected.product"
          @hide="modalHide"
        />
      </div>
    </template>
  </Component>
</template>

<script>
import {
  dateFilter,
  numberFilter,
  currencyFilter,
  phoneFilter,
  feelingFilter,
  capitalizeFilter,
  startCaseFilter,
  accountCardNumberFilter
} from '@/utils/filters'
import { layoutMixin } from '@/utils/mixins'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import BaseLoaderDefault from '@/components/base/BaseLoaderDefault'
import CommonPageFilter from '@/components/common/CommonPageFilter'
import CommonLeadDetails from '@/components/common/CommonLeadDetails'
import CommonLeadHistory from '@/components/common/CommonLeadHistory'
import CommonModalLeadJourney from '@/components/common/CommonModalLeadJourney'
import ClickOutside from 'vue-click-outside'
import { mapState, mapGetters, mapMutations } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'

export default {
  name: 'LeadsInsights',

  components: {
    CommonPageFilter,
    BasePaneCard,
    BaseEmptyState,
    BaseLoaderDefault,
    CommonLeadDetails,
    CommonLeadHistory,
    CommonModalLeadJourney
  },

  directives: {
    ClickOutside
  },

  filters: {
    date: dateFilter,
    number: numberFilter,
    currency: currencyFilter,
    phone: phoneFilter,
    feeling: feelingFilter,
    capitalize: capitalizeFilter,
    startCase: startCaseFilter,
    accountCardNumber: accountCardNumberFilter
  },

  mixins: [
    layoutMixin
  ],

  data () {
    return {
      table: {
        columns: [
          {
            id: 1,
            label: 'Lead',
            isVisible: true,
            isActive: true
          },
          {
            id: 2,
            label: 'Produto',
            isVisible: false,
            isActive: true
          },
          {
            id: 3,
            label: 'Status',
            isVisible: true,
            isActive: true
          },
          {
            id: 4,
            label: 'Sentimento',
            isVisible: false,
            isActive: false
          },
          {
            id: 5,
            label: 'Csat',
            isVisible: false,
            isActive: true
          },
          {
            id: 6,
            label: 'Origem',
            isVisible: false,
            isActive: true
          },
          {
            id: 7,
            label: 'Plugin',
            isVisible: false,
            isActive: true
          },
          {
            id: 8,
            label: 'Dados pessoais',
            isVisible: false,
            isActive: true
          }
        ],

        rows: []
      },
      platformAttributes: null,
      pageItems: {
        start: 0,
        total: 0
      },
      isLocalLoading: false,
      isOpenColumnCustomize: false,
      leadSelected: {
        id: null,
        historyId: null,
        status: null,
        fullName: null,
        product: null
      },
      isOpenLeadDetails: false,
      isOpenLeadHistory: false
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'isLoading',
      'pageFilter'
    ]),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    columnsActive () {
      return this.table.columns.filter(column => column.isActive === true)
    },

    pageItemsMessage () {
      const { total } = this.pageItems
      const { length: totalVisible } = this.table.rows

      return total
        ? `Mostrando ${totalVisible} de ${numberFilter(total)}`
        : 'Nenhum item para mostrar'
    },

    hasMorePageItems () {
      const { total } = this.pageItems
      const { length: totalVisible } = this.table.rows

      return totalVisible < total
    },

    leadHistoryHeading () {
      const { fullName: name } = this.leadSelected

      return name
        ? startCaseFilter(name)
        : ''
    },

    leadHistorySubheading () {
      const { id, product } = this.leadSelected
      let subheading = ''

      if (id) {
        subheading = `<div>ID #${id}</div>`
      }

      if (product) {
        subheading += `<div>${product}</div>`
      }

      return subheading
    },

    leadDetailsHeading () {
      const { fullName: name } = this.leadSelected

      return name
        ? startCaseFilter(name)
        : ''
    },

    leadDetailsSubheading () {
      const { id, created } = this.leadSelected
      let subheading = ''

      if (id) {
        subheading = `<div>ID #${id}</div>`
      }

      if (created) {
        subheading += `<div>${dateFilter(created, 'datetime-short')}</div>`
      }

      return subheading
    }
  },

  watch: {
    pageFilter () {
      this.fetchLeads(true)
    }
  },

  created () {
    this.restoreCustomizeColumn()
  },

  mounted () {
    this.$root.$on('pane-tray-close', this.closeLeadHistory)
    this.$root.$on('pane-tray-close', this.closeLeadDetails)
  },

  methods: {
    ...mapMutations('base', {
      setIsLoading: 'SET_IS_LOADING'
    }),

    toggleCustomizeColumn () {
      this.isOpenColumnCustomize = !this.isOpenColumnCustomize
    },

    closeCustomizeColumn () {
      this.isOpenColumnCustomize = false
    },

    chooseColumn (columnId) {
      const column = this.table.columns.find(item => item.id === columnId)

      column.isVisible = !column.isVisible

      Object.assign(this.table.columns, column)

      this.storeCustomizeColumn()
    },

    storeCustomizeColumn () {
      const columnsVisible = this.columnsActive
        .filter(column => column.isVisible === true)
        .map(column => column.id)

      localStorage.setItem('leadsColumnsVisible', JSON.stringify(columnsVisible))
    },

    restoreCustomizeColumn () {
      const columnsVisible = JSON.parse(localStorage.getItem('leadsColumnsVisible'))

      if (!columnsVisible) {
        return
      }

      this.table.columns.forEach(column => {
        column.isVisible = columnsVisible.includes(column.id)
      })
    },

    fetchPlatformAttributes () {
      const url = `${this.mainApi}lead/attribute/${this.platformId}`

      this.setIsLoading(true)

      return axios
        .get(url)
        .then(({ data }) => {
          this.platformAttributes = data

          return data
        })
        .catch(error => {
          throw error
        })
    },

    resetLeads () {
      const pageItems = {
        start: 0,
        total: 0
      }

      this.table.rows = []

      Object.assign(this.pageItems, pageItems)
    },

    updateLeadsPayload (payload) {
      const columnsIndexes = {
        name: 'name',
        email: 'email',
        phone: 'phone',
        cpf:'cpf',
        dateRange: 'clh_last-wc-created_at',
        action: 'clh_last-wc-fk_id_action',
        plugin: 'clh_lead-wc-plugin',
        product: 'product',
        status: 'clh_last-wc-fk_id_status'
      }
      const columnsToAddConditionalOR = [
        'action',
        'status'
      ]

      Object
        .entries(this.pageFilter)
        .forEach(filter => {
          const [key, value] = filter
          const ignoredColumnsFilters = [
            'csat',
            'feeling'
          ]

          if (Boolean(value.length)) {
            if (ignoredColumnsFilters.includes(key)) {
              payload[key] = value
            } else {
              const columnIndex = payload.columns.findIndex(item => item.data === columnsIndexes[key])
              const columnUpdate = {
                search: { value }
              }

              if (columnsToAddConditionalOR.includes(key)) {
                columnUpdate.search.cond = 'OR'
              }

              if (key === 'dateRange') {
                let [dateStart, dateEnd] = value

                dateEnd = dateFilter(dayjs(dateEnd).add(1, 'day'), 'base')

                columnUpdate.search.value = [dateStart, dateEnd].map(date => `${date} 03:00:00`)
              }

              Object.assign(payload.columns[columnIndex], columnUpdate)
            }
          }

          return payload
        })
    },

    async fetchLeads (triggeredByWatch) {
      const url = `${this.mainApi}solution/datatable`
      const payload = {
        draw: 1,
        columns: [
          {
            data: 'cl-wc-id_lead',
            name: 'cl-wc-id_lead',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_lead-wc-id_lead_history',
            name: 'clh_lead-wc-id_lead_history',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'name',
            name: 'name',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'phone',
            name: 'phone',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'email',
            name: 'email',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'details',
            name: 'details',
            searchable: true,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_lead-wc-created_at',
            name: 'clh_lead-wc-created_at',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cs_status-wc-label',
            name: 'cs_status-wc-label',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_last-wc-fk_id_status',
            name: 'clh_last-wc-fk_id_status',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_last-wc-fk_id_action',
            name: 'clh_last-wc-fk_id_action',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_last-wc-created_at',
            name: 'clh_last-wc-created_at',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cl_export-wc-worked_at',
            name: 'cl_export-wc-worked_at',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_lead-wc-plugin',
            name: 'clh_lead-wc-plugin',
            searchable: true,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cl_export-wc-fk_id_channel',
            name: 'cl_export-wc-fk_id_channel',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_lead-wc-export_date',
            name: 'clh_lead-wc-export_date',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_last-wc-id_lead_history',
            name: 'clh_last-wc-id_lead_history',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'age_group',
            name: 'age_group',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'birth_date',
            name: 'birth_date',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'campus',
            name: 'campus',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'contract_generation_date',
            name: 'contract_generation_date',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'course',
            name: 'course',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cpf',
            name: 'cpf',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'date_renovation',
            name: 'date_renovation',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'expected_graduation_month',
            name: 'expected_graduation_month',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'follow_up',
            name: 'follow_up',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'fup_consultor',
            name: 'fup_consultor',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'graduation_type',
            name: 'graduation_type',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'home_phone',
            name: 'home_phone',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'institution_group',
            name: 'institution_group',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'latest_status',
            name: 'latest_status',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'modality',
            name: 'modality',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'pendencies',
            name: 'pendencies',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'product',
            name: 'product',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'score',
            name: 'score',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'semester_last_contract',
            name: 'semester_last_contract',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'sex',
            name: 'sex',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'state',
            name: 'state',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'status_date',
            name: 'status_date',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'university',
            name: 'university',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'value',
            name: 'value',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'channel',
            name: 'channel',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'date_scheduling',
            name: 'date_scheduling',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'note_internal',
            name: 'note_internal',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'tabulation',
            name: 'tabulation',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cs_channel-wc-label',
            name: 'cs_channel-wc-label',
            searchable: true,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cs_user-wc-name',
            name: 'cs_user-wc-name',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'pabxf-wc-statusLigacaoDesc',
            name: 'pabxf-wc-statusLigacaoDesc',
            searchable: true,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'pabxf-wc-idStatusLigacao',
            name: 'pabxf-wc-idStatusLigacao',
            searchable: false,
            orderable: true,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cae-wc-evaluation',
            name: 'cae-wc-evaluation',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'cae-wc-details',
            name: 'cae-wc-details',
            searchable: false,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_lead-wc-referer',
            name: 'clh_lead-wc-referer',
            searchable: true,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 'clh_lead-wc-origin',
            name: 'clh_lead-wc-origin',
            searchable: true,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          },
          {
            data: 52,
            name: '',
            searchable: true,
            orderable: false,
            search: {
              value: '',
              regex: false
            }
          }
        ],
        order: [
          {
            column: 0,
            dir: 'desc'
          }
        ],
        start: this.pageItems.start,
        length: 10,
        search: {
          value: '',
          regex: false
        },
        filterAttr: false,
        groupByHistory: false,
        invalidLeads: false,
        id_user: this.userId,
        id_platform: this.platformId,
        platform_attributes: this.platformAttributes || await this.fetchPlatformAttributes(),
        show_attributes: [
          4,
          1,
          2,
          3,
          5
        ],
        table_filter_apply: [],
        fetchSentiments: false
      }

      this.updateLeadsPayload(payload)

      if (triggeredByWatch) {
        this.setIsLoading(true)
        this.resetLeads()
      }

      this.isLocalLoading = true

      try {
        const response = await axios.post(url, payload)
        const { recordsTotal, data: rows } = response.data

        if (this.$auth.user().platform_name === 'demo') {
          let filteredRows = rows;
          filteredRows = rows.filter(row => row.name != null);

          this.table.rows = [
            ...this.table.rows,
            ...filteredRows
          ]
        } else {
          this.table.rows = [
            ...this.table.rows,
            ...rows
          ]
        }

        this.pageItems.total = rows.length < 10
          ? this.table.rows.length
          : recordsTotal
      } catch (error) {
        this.resetLeads()

        throw error
      } finally {
        if (this.isLoading) {
          this.setIsLoading(false)
        }

        this.isLocalLoading = false
      }
    },

    fetchMorePageItems () {
      if (!this.hasMorePageItems) {
        return
      }

      this.pageItems.start += 10

      this.fetchLeads()
    },

    selectLead (lead) {
      const leadSelected = {
        id: lead['cl-wc-id_lead'],
        historyId: lead['clh_last-wc-id_lead_history'],
        statusId: lead['clh_last-wc-fk_id_status'],
        statusLabel: lead['cs_status-wc-label'],
        fullName: lead['name'],
        product: this.getProduct(lead),
        created: lead['clh_lead-wc-created_at']
      }

      Object.assign(this.leadSelected, leadSelected)
    },

    openLeadDetails (lead) {
      this.selectLead(lead)

      this.isOpenLeadDetails = true
      this.isOpenLeadHistory = false
    },

    openLeadHistory (lead) {
      this.selectLead(lead)

      this.isOpenLeadHistory = true
      this.isOpenLeadDetails = false
    },

    openLeadJourney (lead) {
      const statusIgnored = [1]

      this.selectLead(lead)

      if (statusIgnored.includes(this.leadSelected.statusId)) {
        return
      }

      this.$bvModal.show('modalLeadJourney')
    },

    modalHide () {
      const leadSelected = {
        id: null,
        historyId: null,
        statusId: null,
        statusLabel: null,
        fullName: null,
        product: null
      }

      Object.assign(this.leadSelected, leadSelected)
    },

    getProduct(row) {
      const value = row['product'] || row['details']

      return accountCardNumberFilter(value)
    },

    getFeeling (feeling) {
      if (!feeling) {
        return '-'
      }

      const label = feelingFilter(feeling)
      let emoji = '😐'

      switch (feeling) {
        case 'negative':
          emoji = '😞'
          break;

        case 'positive':
          emoji = '🙂'
      }

      return `${emoji} ${capitalizeFilter(label)}`
    },

    getStatusTooltip (statusId) {
      const statusExceptions = [1]

      if (statusExceptions.includes(statusId)) {
        return ''
      }

      return 'Acesse a<br/>jornada completa'
    },

    closeLeadDetails () {
      this.isOpenLeadDetails = false
    },

    closeLeadHistory () {
      this.isOpenLeadHistory = false
    }
  }
}
</script>

<style lang="scss" scoped>
.leads {
  height: 100%;

  &__table-wrapper {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: separate;

    thead {
      th {
        position: relative;
        padding: 20px;
        vertical-align: middle;
        border: 1px dashed nth($colors-custom, 2);
        border: {
          left: 0;
          right: 0;
        }
        color: nth($colors-custom, 1);
        font: {
          weight: 800;
          size: 17px;
        }

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          right: 0;
          display: block;
          width: 1px;
          height: 40px;
          transform: translateY(-50%);
          background: nth($colors-custom, 2);
        }

        &:first-child {
          border-left: 1px dashed nth($colors-custom, 2);
          border-radius: 20px 0 0 20px;
        }

        &:last-child {
          width: 0;
          border-right: 1px dashed nth($colors-custom, 2);
          border-radius: 0 20px 20px 0;

          &::after {
            display: none;
          }
        }
      }
    }

    tbody {
      tr {
        &:not(:first-child) {
          td {
            border-top: 1px solid rgba(nth($colors-custom, 2), 0.4);
          }
        }
      }

      td {
        padding: 30px 20px;
        vertical-align: middle;
        font: {
          weight: 400;
          size: 14px;
        }
        line-height: 130%;
      }
    }

    &__column {
      &-customize {
        &--open & {
          &__trigger {
            background: rgba(nth($colors-custom, 1), 0.1);
            transform: rotate(45deg);
          }

          &__menu {
            display: block;
          }
        }

        &__trigger {
          display: flex;
          width: 40px;
          height: 40px;
          justify-content: center;
          align-items: center;
          border: 0;
          border-radius: 50%;
          transition: all 0.2s;
          background:  none;
          font: {
            weight: 600;
            size: 35px;
          }
        }

        &__menu {
          z-index: 1;
          position: absolute;
          top: 40px;
          left: 0;
          display: none;
          min-width: 200px;
          padding: 20px;
          border: 2px solid nth($colors-custom, 3);
          border-radius: 15px 0 15px 15px;
          box-shadow: 0 0 15px 10px rgba(23, 37, 66, 0.03);
          transform: translateX(-95%);
          transition: all 0.2s;
          background: #fff;
        }

        &__heading {
          margin-bottom: 10px;
          font: {
            weight: 700;
            size: 15px;
          }

        }

        &__list {
          padding: 0;

          &__item {
            display: flex;
            align-items: center;
            margin: 5px 0;
            cursor: pointer;
            color: nth($colors-custom, 1);
            font: {
              weight: 400;
              size: 15px;
            }
            line-height: 1.5;

            &::before {
              content: '';
              display: block;
              width: 14px;
              height: 14px;
              margin-right: 10px;
              border-radius: 50%;
              background: nth($colors-custom, 5);
            }

            &--disabled {
              opacity: 0.4;
              pointer-events: none;
            }

            &--selected {
              color: nth($colors-custom, 1);
              font-weight: 500;

              &::before {
                background: nth($colors-custom, 2);
              }
            }
          }
        }
      }

      // Lead
      &-1 {
        min-width: 230px;

        &__actions {
          margin-bottom: 5px;
          white-space: nowrap;
        }

        &__button {
          display: inline-flex;
          height: 25px;
          gap: 5px;
          align-items: center;
          padding: 0 15px;
          border: 1px solid nth($colors-custom, 1);
          border-radius: 2em;
          transition: all 0.1s;
          background: none;
          font: {
            weight: 600;
            size: 12px;
          }

          &:not(:first-child) {
            margin-left: 7px;
          }

          &:hover {
            $color: rgba(nth($colors-custom, 2), 0.5);

            border-color: $color;
            background-color: $color;
          }

          svg {
            width: 12px;
          }

          &--conversation {
            svg {
              fill: nth($colors-custom, 1);
            }
          }
        }

        &__name {
          font-weight: 700;
        }
      }

      // Produto
      &-2 {
        min-width: 260px;

        &__description {
          font-weight: 600;
        }
      }

      // Status
      &-3 {
        min-width: 140px;

        &__tag {
          margin-bottom: 10px;
        }

        &__amount {
          margin-bottom: 5px;
          font: {
            weight: 400;
            size: 15px;
          }
        }

        &__when {
          font-weight: 300;
        }
      }

      // Sentimento
      &-4 {
        &__label {
          font-weight: 600;
          white-space: nowrap;

          &::first-letter {
            margin-right: 3px;
          }
        }
      }

      // Csat
      &-5 {
        &__rating {
          display: flex;
          align-items: center;

          &__star {
            width: 20px;
            height: 15px;
            margin: 0 2px;
            background: {
              image: url('~@/assets/img/icon-star-rating-outline.svg');
              position: 50% 50%;
              repeat: no-repeat;
              size: 100% auto;
            }
          }

          &--1 & {
            &__star {
              &:first-child {
                background-image: url('~@/assets/img/icon-star-rating.svg');
              }
            }
          }

          &--2 & {
            &__star {
              &:nth-child(-n + 2) {
                background-image: url('~@/assets/img/icon-star-rating.svg');
              }
            }
          }

          &--3 & {
            &__star {
              &:nth-child(-n + 3) {
                background-image: url('~@/assets/img/icon-star-rating.svg');
              }
            }
          }

          &--4 & {
            &__star {
              &:nth-child(-n + 4) {
                background-image: url('~@/assets/img/icon-star-rating.svg');
              }
            }
          }

          &--5 & {
            &__star {
              background-image: url('~@/assets/img/icon-star-rating.svg');
            }
          }
        }
      }

      // Origin
      &-6 {
        &__origin {
          font-weight: 600;
        }

        &__referer {
          margin-top: 5px;
          color: nth($colors-custom, 1);
          font-weight: 300;
          transition: all 0.1s;

          &:hover {
            color: nth($colors-custom, 2);
          }
        }
      }

      // Plugin
      &-7 {
        &__label {
          font-weight: 600;
        }
      }

      // Dados pessoais
      &-8 {
        font-weight: 300;
        white-space: nowrap;

        strong {
          font-weight: 600;
        }
      }
    }
  }

  &__page-items {
    margin-top: 40px;
    text-align: center;

    &__info {
      font: {
        weight: 400;
        size: 14px;
      }
    }

    &__more {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 140px;
      height: 40px;
      margin-top: 20px;
      border: 2px solid nth($colors-custom, 2);
      border-radius: 2em;
      background: none;
      color: nth($colors-custom, 2);
      font-size: 14px;

      &:disabled {
        opacity: 0.5;
      }

      &--off {
        pointer-events: none;
      }

      svg {
        width: 40px;
        fill: nth($colors-custom, 2);
      }
    }
  }
}
</style>
